import React, { useState, useRef, useCallback, useEffect } from "react";
import { motion } from "framer-motion";
import {
  Upload,
  FileText,
  X,
  Loader2,
  Check,
  User,
  Briefcase,
  Folder,
  FolderPlus,
  ChevronRight,
} from "lucide-react";
import { documentsApi, clientsApi, mattersApi, foldersApi } from "../../services/api";
import PremiumModal from "../ui/PremiumModal";
import SearchableSelect, { SelectOption } from "../ui/SearchableSelect";
import QuickCreateClientModal from "../ui/QuickCreateClientModal";
import QuickCreateMatterModal from "../ui/QuickCreateMatterModal";
import CreateFolderModal from "./CreateFolderModal";
import AiMatterSuggestion from "../ui/AiMatterSuggestion";

const typeOptions: SelectOption[] = [
  { value: "intake_upload", label: "Intake Upload" },
  { value: "retainer", label: "Retainer" },
  { value: "correspondence", label: "Correspondence" },
  { value: "pleading", label: "Pleading" },
  { value: "evidence", label: "Evidence" },
  { value: "invoice_pdf", label: "Invoice PDF" },
  { value: "receipt", label: "Receipt" },
  { value: "closing_statement", label: "Closing Statement" },
  { value: "title_search", label: "Title Search" },
  { value: "mortgage_instructions", label: "Mortgage Instructions" },
  { value: "other", label: "Other" },
];

interface Props {
  onClose: () => void;
  onComplete: () => void;
  defaultMatterId?: string;
  defaultClientId?: string;
  folderId?: string;
}

interface BreadcrumbItem {
  _id: string;
  name: string;
}

const DocumentUploadModal: React.FC<Props> = ({
  onClose,
  onComplete,
  defaultMatterId,
  defaultClientId,
  folderId,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const initRef = useRef(false);

  // File & metadata
  const [file, setFile] = useState<File | null>(null);
  const [title, setTitle] = useState("");
  const [type, setType] = useState("other");
  const [description, setDescription] = useState("");
  const [tagsInput, setTagsInput] = useState("");
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState("");
  const [isDragOver, setIsDragOver] = useState(false);

  // Client / Matter selection
  const [clientId, setClientId] = useState<string | null>(defaultClientId || null);
  const [matterId, setMatterId] = useState<string | null>(defaultMatterId || null);
  const [clientOptions, setClientOptions] = useState<SelectOption[]>([]);
  const [matterOptions, setMatterOptions] = useState<SelectOption[]>([]);
  const [clientsLoading, setClientsLoading] = useState(false);
  const [mattersLoading, setMattersLoading] = useState(false);

  // Folder resolution
  const [resolvedFolderId, setResolvedFolderId] = useState<string | null>(folderId || null);
  const [folderBreadcrumb, setFolderBreadcrumb] = useState<BreadcrumbItem[]>([]);
  const [subfolders, setSubfolders] = useState<BreadcrumbItem[]>([]);
  const [folderLoading, setFolderLoading] = useState(false);

  // Quick-create modals
  const [showCreateClient, setShowCreateClient] = useState(false);
  const [showCreateMatter, setShowCreateMatter] = useState(false);
  const [showCreateFolder, setShowCreateFolder] = useState(false);

  // ── Load breadcrumb + subfolders for a folder ──
  const loadBreadcrumbAndSubfolders = useCallback(async (fId: string) => {
    const [ancestorsRes, contentsRes] = await Promise.all([
      foldersApi.getAncestors(fId),
      foldersApi.getContents(fId),
    ]);
    const ancestors = ancestorsRes.data.data || ancestorsRes.data || [];
    setFolderBreadcrumb(ancestors.map((f: any) => ({ _id: f._id, name: f.name })));
    const subs = contentsRes.data.data?.subfolders || contentsRes.data?.subfolders || [];
    setSubfolders(subs.map((f: any) => ({ _id: f._id, name: f.name })));
  }, []);

  // ── Load clients on mount ──
  useEffect(() => {
    setClientsLoading(true);
    clientsApi
      .list({ limit: 100 })
      .then((res) => {
        const data = res.data.data || res.data || [];
        setClientOptions(data.map((c: any) => ({ value: c._id, label: c.displayName })));
      })
      .catch(() => {})
      .finally(() => setClientsLoading(false));
  }, []);

  // ── Initialize from folderId prop (reverse-resolve client/matter) ──
  useEffect(() => {
    if (initRef.current || !folderId) return;
    initRef.current = true;

    const init = async () => {
      setFolderLoading(true);
      try {
        const res = await foldersApi.get(folderId);
        const folder = res.data.data || res.data;
        if (folder.clientId) setClientId(String(folder.clientId));
        if (folder.matterId) setMatterId(String(folder.matterId));
        await loadBreadcrumbAndSubfolders(folderId);
      } catch {
        // folder may not exist, ignore
      } finally {
        setFolderLoading(false);
      }
    };
    init();
  }, [folderId, loadBreadcrumbAndSubfolders]);

  // ── Load matters when client changes ──
  useEffect(() => {
    if (!clientId) {
      setMatterOptions([]);
      return;
    }
    setMattersLoading(true);
    mattersApi
      .list({ clientId, limit: 100 })
      .then((res) => {
        const data = res.data.data || res.data || [];
        setMatterOptions(
          data.map((m: any) => ({
            value: m._id,
            label: m.title,
            description: m.matterNumber || undefined,
          }))
        );
      })
      .catch(() => {})
      .finally(() => setMattersLoading(false));
  }, [clientId]);

  // ── Resolve folder when client/matter changes ──
  useEffect(() => {
    // Skip if we initialized from folderId prop (already resolved)
    if (!initRef.current && folderId) return;

    const resolveFolder = async () => {
      setFolderLoading(true);
      try {
        if (matterId && clientId) {
          // Look up the matter folder
          const res = await foldersApi.list({ clientId, matterId, flat: "true" });
          const folders = res.data.data || res.data || [];
          if (folders.length > 0) {
            const folder = folders[0];
            setResolvedFolderId(folder._id);
            await loadBreadcrumbAndSubfolders(folder._id);
            return;
          }
          // No matter folder found  -  auto-create it
          const clientFolderRes = await foldersApi.list({ clientId, matterId: "null", flat: "true" });
          const clientFolders = clientFolderRes.data.data || clientFolderRes.data || [];
          const clientFolder = clientFolders.find((f: any) => !f.matterId);
          if (clientFolder) {
            const matterLabel = matterOptions.find((m) => m.value === matterId)?.label || "New Matter";
            const newFolderRes = await foldersApi.create({
              name: matterLabel,
              parentId: clientFolder._id,
              clientId,
              matterId,
            });
            const created = newFolderRes.data.data || newFolderRes.data;
            setResolvedFolderId(created._id);
            await loadBreadcrumbAndSubfolders(created._id);
            return;
          }
        }

        if (clientId && !matterId) {
          // Look up the client's root folder (where matterId is null)
          const res = await foldersApi.list({ clientId, matterId: "null", flat: "true" });
          const folders = res.data.data || res.data || [];
          const clientFolder = folders.find((f: any) => !f.matterId);
          if (clientFolder) {
            setResolvedFolderId(clientFolder._id);
            await loadBreadcrumbAndSubfolders(clientFolder._id);
            return;
          }
        }

        if (!clientId) {
          if (folderId) {
            setResolvedFolderId(folderId);
            await loadBreadcrumbAndSubfolders(folderId);
          } else {
            setResolvedFolderId(null);
            setFolderBreadcrumb([]);
            setSubfolders([]);
          }
        }
      } catch {
        // resolution failed, ignore
      } finally {
        setFolderLoading(false);
      }
    };

    resolveFolder();
  }, [clientId, matterId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Client change handler ──
  const handleClientChange = (val: string | null) => {
    setClientId(val);
    setMatterId(null);
  };

  // ── Subfolder / breadcrumb navigation ──
  const handleSubfolderClick = async (subfolderId: string) => {
    setResolvedFolderId(subfolderId);
    setFolderLoading(true);
    try {
      await loadBreadcrumbAndSubfolders(subfolderId);
    } finally {
      setFolderLoading(false);
    }
  };

  const handleBreadcrumbClick = async (fId: string) => {
    setResolvedFolderId(fId);
    setFolderLoading(true);
    try {
      await loadBreadcrumbAndSubfolders(fId);
    } finally {
      setFolderLoading(false);
    }
  };

  // ── Inline creation callbacks ──
  const handleClientCreated = (client: { _id: string; displayName: string }) => {
    setClientOptions((prev) => [...prev, { value: client._id, label: client.displayName }]);
    setClientId(client._id);
    setShowCreateClient(false);
  };

  const handleMatterCreated = (matter: { _id: string; title: string; matterNumber: string }) => {
    setMatterOptions((prev) => [
      ...prev,
      { value: matter._id, label: matter.title, description: matter.matterNumber },
    ]);
    setMatterId(matter._id);
    setShowCreateMatter(false);
  };

  const handleFolderCreated = async () => {
    setShowCreateFolder(false);
    if (resolvedFolderId) {
      setFolderLoading(true);
      try {
        await loadBreadcrumbAndSubfolders(resolvedFolderId);
      } finally {
        setFolderLoading(false);
      }
    }
  };

  // ── File handling ──
  const handleFileSelect = useCallback(
    (selectedFile: File) => {
      setFile(selectedFile);
      if (!title) {
        const name = selectedFile.name.replace(/\.[^/.]+$/, "");
        setTitle(name);
      }
    },
    [title]
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragOver(false);
      if (e.dataTransfer.files?.[0]) {
        handleFileSelect(e.dataTransfer.files[0]);
      }
    },
    [handleFileSelect]
  );

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = () => {
    setIsDragOver(false);
  };

  // ── Upload ──
  const handleUpload = async () => {
    if (!file) {
      setError("Please select a file");
      return;
    }
    if (!title.trim()) {
      setError("Please enter a title");
      return;
    }

    setUploading(true);
    setProgress(0);
    setError("");

    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("title", title.trim());
      if (clientId) formData.append("clientId", clientId);
      if (matterId) formData.append("matterId", matterId);
      if (resolvedFolderId) formData.append("folderId", resolvedFolderId);
      formData.append("type", type);
      if (description) formData.append("description", description);
      if (tagsInput.trim()) {
        const tags = tagsInput
          .split(",")
          .map((t) => t.trim())
          .filter(Boolean);
        formData.append("tags", JSON.stringify(tags));
      }

      const progressInterval = setInterval(() => {
        setProgress((prev) => Math.min(prev + 10, 90));
      }, 200);

      await documentsApi.upload(formData);

      clearInterval(progressInterval);
      setProgress(100);

      setTimeout(() => {
        onComplete();
      }, 500);
    } catch (err: any) {
      setError(err.response?.data?.error || "Upload failed. Please try again.");
      setUploading(false);
      setProgress(0);
    }
  };

  const footerContent = (
    <div className="flex items-center justify-end gap-3">
      <button
        onClick={onClose}
        disabled={uploading}
        className="px-4 py-2.5 text-sm font-medium text-slate-700 bg-white border border-slate-200 rounded-xl hover:bg-slate-50 transition-colors disabled:opacity-50"
      >
        Cancel
      </button>
      <button
        onClick={handleUpload}
        disabled={uploading || !file}
        className="flex items-center gap-2 px-5 py-2.5 text-sm font-medium text-white rounded-xl transition-colors disabled:opacity-50"
        style={{ backgroundColor: "var(--color-primary)" }}
        onMouseEnter={(e) => {
          if (!uploading) e.currentTarget.style.backgroundColor = "var(--color-primary-hover)";
        }}
        onMouseLeave={(e) => {
          if (!uploading) e.currentTarget.style.backgroundColor = "var(--color-primary)";
        }}
      >
        {uploading ? (
          <>
            <Loader2 className="h-4 w-4 animate-spin" />
            Uploading...
          </>
        ) : progress === 100 ? (
          <>
            <Check className="h-4 w-4" />
            Done
          </>
        ) : (
          <>
            <Upload className="h-4 w-4" />
            Upload
          </>
        )}
      </button>
    </div>
  );

  return (
    <>
      <PremiumModal
        open={true}
        onClose={onClose}
        title="Upload Document"
        icon={Upload}
        size="lg"
        footer={footerContent}
      >
        <div className="space-y-4">
          {/* Drag-and-drop Zone */}
          <div
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onClick={() => fileInputRef.current?.click()}
            className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-colors ${
              isDragOver
                ? "border-brand-accent bg-brand-light"
                : file
                  ? "border-brand-accent bg-brand-light/50"
                  : "border-slate-300 hover:border-brand-accent hover:bg-slate-50"
            }`}
          >
            <input
              ref={fileInputRef}
              type="file"
              className="hidden"
              onChange={(e) => {
                if (e.target.files?.[0]) handleFileSelect(e.target.files[0]);
              }}
            />
            {file ? (
              <div className="flex items-center justify-center gap-3">
                <FileText className="h-8 w-8 text-brand-accent" />
                <div className="text-left">
                  <p className="text-sm font-medium" style={{ color: "#1F2937" }}>
                    {file.name}
                  </p>
                  <p className="text-xs text-slate-500">
                    {(file.size / 1024 / 1024).toFixed(2)} MB
                  </p>
                </div>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setFile(null);
                  }}
                  className="p-1 hover:bg-slate-200 rounded transition-colors"
                >
                  <X className="h-4 w-4 text-slate-500" />
                </button>
              </div>
            ) : (
              <>
                <Upload className="h-10 w-10 mx-auto text-slate-400 mb-3" />
                <p className="text-sm text-slate-600 font-medium">
                  Drop your file here, or click to browse
                </p>
                <p className="text-xs text-slate-400 mt-1">
                  PDF, DOC, DOCX, XLS, XLSX, JPG, PNG up to 50 MB
                </p>
              </>
            )}
          </div>

          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Title <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Document title"
              className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-600 transition-colors"
            />
          </div>

          {/* Client Dropdown */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Client</label>
            <SearchableSelect
              options={clientOptions}
              value={clientId}
              onChange={handleClientChange}
              placeholder="Select a client"
              searchPlaceholder="Search clients..."
              icon={User}
              loading={clientsLoading}
              clearable
              addNew={{
                label: "Create new client",
                onAdd: () => setShowCreateClient(true),
              }}
            />
          </div>

          {/* Matter Dropdown */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Matter</label>
            <SearchableSelect
              options={matterOptions}
              value={matterId}
              onChange={(val) => setMatterId(val)}
              placeholder={clientId ? "Select a matter" : "Select a client first"}
              searchPlaceholder="Search matters..."
              icon={Briefcase}
              loading={mattersLoading}
              disabled={!clientId}
              clearable
              addNew={
                clientId
                  ? {
                      label: "Create new matter",
                      onAdd: () => setShowCreateMatter(true),
                    }
                  : undefined
              }
            />
            {/* AI matter suggestion when title is set but no matter selected */}
            {!matterId && title.trim().length > 5 && clientId && (
              <AiMatterSuggestion
                content={title}
                contentType="document"
                clientId={clientId}
                onSelect={(id) => setMatterId(id)}
              />
            )}
          </div>

          {/* Destination Folder */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">
              Destination Folder
            </label>
            <div className="rounded-xl border border-slate-200 bg-slate-50/50 p-3">
              {/* Breadcrumb */}
              {folderBreadcrumb.length > 0 ? (
                <div className="flex items-center gap-1 text-sm flex-wrap">
                  <Folder className="h-3.5 w-3.5 text-amber-500 flex-shrink-0" />
                  {folderBreadcrumb.map((crumb, idx) => (
                    <React.Fragment key={crumb._id}>
                      {idx > 0 && (
                        <ChevronRight className="h-3 w-3 text-slate-300 flex-shrink-0" />
                      )}
                      <button
                        type="button"
                        onClick={() => handleBreadcrumbClick(crumb._id)}
                        className={`transition-colors ${
                          idx === folderBreadcrumb.length - 1
                            ? "font-medium text-brand"
                            : "text-slate-500 hover:text-brand-accent"
                        }`}
                      >
                        {crumb.name}
                      </button>
                    </React.Fragment>
                  ))}
                </div>
              ) : folderLoading ? (
                <div className="flex items-center gap-2 text-sm text-slate-400">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  <span>Resolving folder...</span>
                </div>
              ) : (
                <div className="flex items-center gap-2 text-sm text-slate-400">
                  <Folder className="h-3.5 w-3.5" />
                  <span>Select a client to set the destination folder</span>
                </div>
              )}

              {/* Subfolder chips */}
              {subfolders.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2.5 pt-2.5 border-t border-slate-200/70">
                  <span className="text-xs text-slate-400 self-center mr-1">Subfolders:</span>
                  {subfolders.map((sf) => (
                    <button
                      key={sf._id}
                      type="button"
                      onClick={() => handleSubfolderClick(sf._id)}
                      className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-white border border-slate-200 rounded-lg text-xs font-medium text-slate-700 hover:border-brand-accent hover:bg-brand-light transition-colors"
                    >
                      <Folder className="h-3 w-3 text-amber-500" />
                      {sf.name}
                    </button>
                  ))}
                </div>
              )}

              {/* New Folder button */}
              {resolvedFolderId && !folderLoading && (
                <button
                  type="button"
                  onClick={() => setShowCreateFolder(true)}
                  className="inline-flex items-center gap-1.5 mt-2 px-2.5 py-1 text-xs font-medium rounded-lg transition-colors hover:bg-brand-light"
                  style={{ color: "var(--color-primary)" }}
                >
                  <FolderPlus className="h-3.5 w-3.5" />
                  New Folder
                </button>
              )}
            </div>
          </div>

          {/* Type Dropdown */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Document Type</label>
            <SearchableSelect
              options={typeOptions}
              value={type}
              onChange={(val) => setType(val || "other")}
              placeholder="Select document type"
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Description</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Brief description (optional)"
              rows={3}
              className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-600 transition-colors resize-none"
            />
          </div>

          {/* Tags */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Tags</label>
            <input
              type="text"
              value={tagsInput}
              onChange={(e) => setTagsInput(e.target.value)}
              placeholder="Comma-separated tags (e.g. urgent, draft)"
              className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-600 transition-colors"
            />
          </div>

          {/* Error */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-sm text-red-700">
              {error}
            </div>
          )}

          {/* Progress Bar */}
          {uploading && (
            <div className="space-y-2">
              <div className="flex items-center justify-between text-xs text-slate-500">
                <span>Uploading...</span>
                <span>{progress}%</span>
              </div>
              <div className="w-full bg-slate-200 rounded-full h-2">
                <motion.div
                  className="h-2 rounded-full"
                  style={{ backgroundColor: "var(--color-primary)" }}
                  initial={{ width: 0 }}
                  animate={{ width: `${progress}%` }}
                  transition={{ duration: 0.3 }}
                />
              </div>
            </div>
          )}
        </div>
      </PremiumModal>

      {/* Quick-create modals */}
      <QuickCreateClientModal
        open={showCreateClient}
        onClose={() => setShowCreateClient(false)}
        onCreated={handleClientCreated}
      />

      <QuickCreateMatterModal
        open={showCreateMatter}
        onClose={() => setShowCreateMatter(false)}
        onCreated={handleMatterCreated}
        preselectedClientId={clientId || undefined}
      />

      <CreateFolderModal
        open={showCreateFolder}
        onClose={() => setShowCreateFolder(false)}
        onCreated={handleFolderCreated}
        parentId={resolvedFolderId || undefined}
        clientId={clientId || undefined}
        matterId={matterId || undefined}
      />
    </>
  );
};

export default DocumentUploadModal;
